import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Spin, DatePicker, Card, Modal, Badge, Descriptions, Progress } from "antd";
import ReactEcharts from "echarts-for-react";
import moment from "../../util/momentTimezone";
import GYAxios from "../../util/GYAxios";
import styles from "./BESSDashboard.module.css";
import { useSite } from "../../util/sites";
import Noti from "../../util/noti";
import { useFormatter } from "./../../util/formatter";
import { BESS_PARAMS_ENUM, get_BESS_col_name, combine_cabinet_data, map_daily_data, map_monthly_data } from "./bess_func";
import { getP10GraphOpt, getBarGraphOpt, getCabinetsPowerGraphOpt } from "./BESSGraphOpt";
import { useUser } from "../../util/user";
import { isClientUser } from "../../util/roles";

const BESSDashboard = () => {
  // user
  const user = useUser();
  const userRoles = user?.roles || [];
  const isClient = isClientUser(userRoles);
  // number formatter
  const [ formatNumber ] = useFormatter();
  // site data
  const selectedSite = useSite()?.selectedSite?.value;
  const operationStatusMapper = ptStatus => ([ "LIP", "MSA", "COD" ])[ptStatus - 1] || "N/A";
  const formatOptSince = useMemo(() => {
    let fmtSince = "";
    if(selectedSite.operation_status === 2) {
      fmtSince = `${moment(selectedSite.msa).format('DD/MM/YYYY')}`;
    }
    if(selectedSite.operation_status === 3) {
      fmtSince = `${moment(selectedSite.cod).format('DD/MM/YYYY')}`;
    }
    return fmtSince;
  }, [selectedSite]);

  const hideSoh = useMemo(() => selectedSite?.country === 'VN' && isClient, [selectedSite, isClient]);

  // BESS data
  const [ gyStatus, setGyStatus ] = useState(null);
  const [ optStatus, setOptStatus ] = useState(null);
  const [ rawCurrentData, setRawCurrentData ] = useState(null);
  const currentData = useMemo(() => {
    if (!rawCurrentData || !optStatus || !gyStatus || !selectedSite) return null;
    let currentData = { ...rawCurrentData };
    const socCols = [];
    const sohCols = [];
    const powerCols = [];
    const statusCodeCols = [];
    const statusMsgCols = [];

    for(let i=1; i<=selectedSite.BESS_nb_cabinets; i++) {
      // combine cabinet data and put in the currentData object
      currentData = combine_cabinet_data(currentData, selectedSite.BESS_nb_cabinets);

      // add status message based on status code
      const statusMsgCol = get_BESS_col_name(i, BESS_PARAMS_ENUM.STATUS_MSG);
      const gyStatusCol = get_BESS_col_name(i, BESS_PARAMS_ENUM.GY_STATUS);
      const statusCodeCol = get_BESS_col_name(i, BESS_PARAMS_ENUM.STATUS_CODE);
      const statusObj = optStatus.find(s => parseInt(s.model_status) === parseInt(rawCurrentData[statusCodeCol]));
      const statusMsg = statusObj?.model_status_desc || "Unknown Status Code";
      const gyStatusObj = gyStatus.find(s => parseInt(s.status_id) === parseInt(statusObj?.GY_status_id));
      currentData = {
        ...currentData,
        [statusMsgCol]: statusMsg,
        [gyStatusCol]: gyStatusObj,
      };

      socCols.push(get_BESS_col_name(i, BESS_PARAMS_ENUM.STATE_OF_CHARGE));
      sohCols.push(get_BESS_col_name(i, BESS_PARAMS_ENUM.STATE_OF_HEALTH));
      powerCols.push(get_BESS_col_name(i, BESS_PARAMS_ENUM.POWER));
      statusCodeCols.push(statusCodeCol);
      statusMsgCols.push(statusMsgCol);
    }
    return { ...currentData, _metadata: { socCols, sohCols, powerCols, statusCodeCols, statusMsgCols } };
  }, [  rawCurrentData, optStatus, gyStatus, selectedSite ]);
  const alarm = useMemo(() => {
    if(!currentData) return null;
    for(let i=1; i<=selectedSite.BESS_nb_cabinets; i++) {
      const gyStatusCol = get_BESS_col_name(i, BESS_PARAMS_ENUM.GY_STATUS);
      const gyStatusObj = currentData[gyStatusCol];
      if(gyStatusObj?.trigger_alarm) {
        return { color: gyStatusObj.status_color, name: gyStatusObj.status_name };
      }
    }
    const gyOkStatus = gyStatus.find(s => s.status_id === 0);
    return { color: gyOkStatus.status_color, name: gyOkStatus.status_name };
  }, [ currentData, selectedSite, gyStatus ]);

  const [ rawP10Data, setRawP10Data ] = useState(null);
  const p10Data = useMemo(() => rawP10Data ? rawP10Data.map(row => combine_cabinet_data(row, selectedSite.BESS_nb_cabinets)) : [], [ rawP10Data, selectedSite ]);
  
  const defaultDateRange = useMemo(() => {
    const limitDate = moment(selectedSite?.msa || selectedSite?.cod).startOf('day');
    const last7days = moment().subtract(7, 'day').startOf('day');
    const today = moment().startOf('day');
    let startDate = last7days;
    let endDate = today;
    if(limitDate >= last7days && limitDate <= today) {
      startDate = limitDate;
    }
    if(limitDate > today) {
      startDate = null;
      endDate = null;
    }
    return [startDate, endDate];
  }, [selectedSite]);

  const defaultMonthRange = useMemo(() => {
    const limitDate = moment(selectedSite?.msa || selectedSite?.cod).startOf('month');
    const last3months = moment().subtract(6, 'month').startOf('month');
    const thisMonth = moment().startOf('month');
    let startMonth = last3months;
    let endMonth = thisMonth;
    if(limitDate >= last3months && limitDate <= thisMonth) {
      startMonth = limitDate;
    }
    if(limitDate > thisMonth) {
      startMonth = null;
      endMonth = null;
    }
    return [startMonth, endMonth];
  }, [selectedSite]);

  const [ dateRange, setDateRange ] = useState(defaultDateRange);
  const [ monthRange, setMonthRange ] = useState(defaultMonthRange);

  const p10DateData = useMemo(() => p10Data ? p10Data.map(row => moment(row['dateData']).format('DD/MM HH:mm')) : [], [ p10Data ]);
  const p10SoC      = useMemo(() => p10Data ? p10Data.map(row => row[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.STATE_OF_CHARGE)]) : [], [ p10Data ]);
  const p10Power    = useMemo(() => p10Data ? p10Data.map(row => row[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.POWER)]) : [], [ p10Data ]);
  const p10Volt     = useMemo(() => p10Data ? p10Data.map(row => row[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.VOLTAGE)]) : [], [ p10Data ]);
  const p10Current  = useMemo(() => p10Data ? p10Data.map(row => row[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.CURRENT)]) : [], [ p10Data ]);
  const p10GridPower = useMemo(() => p10Data ? p10Data.map(row => row['grid_power']) : [], [ p10Data ]);
  const p10Temp     = useMemo(() => p10Data ? p10Data.map(row => row[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.TEMPERATURE)]) : [], [ p10Data ]);

  const cabinetP10  = useMemo(() => {
    const cabinetP10Data = {};
    for(let i=1; i<=selectedSite?.BESS_nb_cabinets; i++) {
      cabinetP10Data[i] = {
        dateData: p10Data ? p10Data.map(row => moment(row['dateData']).format('DD/MM HH:mm')) : [],
        soc: p10Data ? p10Data.map(row => row[get_BESS_col_name(i, BESS_PARAMS_ENUM.STATE_OF_CHARGE)]) : [],
        power: p10Data ? p10Data.map(row => row[get_BESS_col_name(i, BESS_PARAMS_ENUM.POWER)]) : [],
        voltage: p10Data ? p10Data.map(row => row[get_BESS_col_name(i, BESS_PARAMS_ENUM.VOLTAGE)]) : [],
        current: p10Data ? p10Data.map(row => row[get_BESS_col_name(i, BESS_PARAMS_ENUM.CURRENT)]) : [],
        temp: p10Data ? p10Data.map(row => row[get_BESS_col_name(i, BESS_PARAMS_ENUM.TEMPERATURE)]) : [],
      };
    }
    return cabinetP10Data;
  }, [ p10Data, selectedSite ]);

  const [ selectedCabinet, setSelectedCabinet ] = useState(null);
  const selectedCabinetP10Data = useMemo(() => {
    if (!selectedCabinet || !cabinetP10[selectedCabinet]) return null;
    return cabinetP10[selectedCabinet];
  }, [ selectedCabinet, cabinetP10 ]);

  const [ rawDailyData, setRawDailyData ] = useState(null);
  const dailyData = useMemo(() => !!rawDailyData?.length ? map_daily_data(rawDailyData) : [], [ rawDailyData ]);

  const [ rawMonthlyData, setRawMonthlyData ] = useState(null);
  const monthlyData = useMemo(() => !!rawMonthlyData?.length ? map_monthly_data(rawMonthlyData) : [], [ rawMonthlyData ]);

  // Disable dates after yesterday
  const disabledDate = useCallback((current) => {
    if (!selectedSite) return false;
    const dateLimit = selectedSite?.msa || selectedSite?.cod;
    return current && (current > moment().endOf('day') || current < moment(dateLimit).startOf('day'));
  }, [ selectedSite ]);

  useEffect(() => {
    // Fetch current BESS data
    const fetchCurrentData = async () => {
      try {
        const response = await GYAxios.get(`/bess/sites/${selectedSite.table_name}/current`);
        const data = response.data?.data;
        setRawCurrentData(data);
      } catch (error) {
        console.error("Error fetching BESS data:", error);
        Noti.create({ type: 'e', title: "Error fetching current BESS data" });
      }
    };
    // BESS model status definitions
    const fetchOptStatus = async () => {
      try {
        const response = await GYAxios.get(`/bess/models/${selectedSite.BESS_model}/status-definition`);
        const data = response.data?.data;
        setOptStatus(data);
      } catch (error) {
        console.error("Error fetching BESS operation status:", error);
        Noti.create({ type: 'e', title: "Error fetching BESS status code definitions" });
      }
    };
    // BESS GY status definitions
    const fetchGyStatus = async () => {
      try {
        const response = await GYAxios.get(`/bess/gy-status-definition`);
        const data = response.data?.data;
        setGyStatus(data);
      } catch (error) {
        console.error("Error fetching BESS GY status:", error);
        Noti.create({ type: 'e', title: "Error fetching BESS GY status definitions" });
      }
    };

    const delayedFetch = setTimeout(() => {
      if (selectedSite?.is_BESS) {
        fetchCurrentData();
        fetchOptStatus();
        fetchGyStatus();
      }
    }, 300); // 300ms delay

    return () => clearTimeout(delayedFetch);
  }, [ selectedSite ]);

  useEffect(() => {
    const getRawP10Data = async () => {
      try {
        const response = await GYAxios.get(`/bess/sites/${selectedSite.table_name}/p10/${dateRange[0].format("YYYY-MM-DD")}/${dateRange[1].format("YYYY-MM-DD")}`);
        const rawP10Data = response.data?.data;
        setRawP10Data(rawP10Data);
      } catch (error) {
        console.error("Error fetching BESS P10 data:", error);
        Noti.create({type: 'e', title: "Error fetching BESS P10 data"});
      }
    };

    const getDailyData = async () => {
      try {
        const response = await GYAxios.get(`/bess/sites/${selectedSite.table_name}/daily/${dateRange[0].format("YYYY-MM-DD")}/${dateRange[1].format("YYYY-MM-DD")}`);
        const dailyData = response.data?.data;
        setRawDailyData(dailyData);
      } catch (error) {
        console.error("Error fetching BESS daily data:", error);
        Noti.create({type: 'e', title: "Error fetching BESS daily data"});
      }
    };

    const getMonthlyData = async () => {
      try {
        const response = await GYAxios.get(`/bess/sites/${selectedSite.table_name}/monthly/${monthRange[0].format("YYYY-MM-DD")}/${monthRange[1].format("YYYY-MM-DD")}`);
        const monthlyData = response.data?.data;
        setRawMonthlyData(monthlyData);
      } catch (error) {
        console.error("Error fetching BESS monthly data:", error);
        Noti.create({type: 'e', title: "Error fetching BESS monthly data"});
      }
    };

    const delayedFetch = setTimeout(() => {
      if(!selectedSite || !selectedSite.is_BESS || !dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
        return;
      }
      getRawP10Data();
      getDailyData();
      if(!monthRange || monthRange.length !== 2 || !monthRange[0] || !monthRange[1]) {
        return;
      }
      getMonthlyData();
    }, 300); // 300ms delay
    return () => clearTimeout(delayedFetch);

  }, [ dateRange, monthRange, selectedSite ]);

  const onDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setRawP10Data(null);
    setRawDailyData(null);
  };

  const onMonthRangeChange = (newRange) => {
    setMonthRange(newRange);
    setRawMonthlyData(null);
  };

  return (
    <>
      <Container fluid>

        {/* Show widgets */}
        {!currentData ? (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <Spin size="large" tip="Loading current BESS data..." className={styles.spin} />
          </div>
        ) : (
          <>
            <div style={{ marginTop: '1em' }}>
              <Card>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5em' }}>
                  <Descriptions
                    bordered
                    size="default"
                    style={{ width: '100%' }}
                  >
                    <Descriptions.Item label="Site Name" span={2}>{selectedSite.site_name}</Descriptions.Item>
                    <Descriptions.Item label="Useful Capacity" span={1}>{formatNumber(selectedSite.BESS_capacity, 2)} kWh</Descriptions.Item>
                    <Descriptions.Item label="Operation status" span={1}>{operationStatusMapper(selectedSite.operation_status)}</Descriptions.Item>
                    <Descriptions.Item label="Operating since" span={1}>{formatOptSince}</Descriptions.Item>
                    <Descriptions.Item label="BESS Status" span={1}><Badge status="success" color={alarm.color} text={alarm.name} /></Descriptions.Item>
                  </Descriptions>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Progress type="circle" percent={formatNumber(currentData[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.STATE_OF_CHARGE, 2)])} format={percent => `${percent}%`} />
                    <div style={{ textAlign: 'center', marginTop: '0.5em', fontWeight: 'bold' }}>State of Charge</div>
                  </div>
                  {!hideSoh ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Progress type="circle" percent={formatNumber(currentData[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.STATE_OF_HEALTH, 2)])} format={percent => `${percent}%`} />
                      <div style={{ textAlign: 'center', marginTop: '0.5em', fontWeight: 'bold' }}>State of Health</div>
                    </div>
                  ) : null}
                </div>
              </Card>
            </div>
            {/* <Row style={{ paddingTop: '1em', display: 'flex' }}>
              <Col sm={12}>
                <div className={styles.widget} style={{ minHeight: '100px', height: '100%', background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', padding: '1em' }} >
                  <div>
                    <div style={{ fontSize: 14, color: '#666666', textAlign: 'left', marginTop: 'auto' }}>
                      Site Name
                    </div>
                    <div style={{ fontSize: 16, color: '#6b7280', textAlign: 'left', fontWeight: 'bold', marginTop: '-1.1rem' }}>
                      {selectedSite.site_name}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, color: '#666666', textAlign: 'left', marginTop: 'auto' }}>
                      Useful Capacity
                    </div>
                    <div style={{ fontSize: 16, color: '#6b7280', textAlign: 'left', fontWeight: 'bold', marginTop: '-1.1rem' }}>
                      {formatNumber(selectedSite.BESS_capacity, 2)} kWh
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, color: '#666666', textAlign: 'left', marginTop: 'auto' }}>
                      Operation status
                    </div>
                    <div style={{ fontSize: 16, color: '#6b7280', textAlign: 'left', fontWeight: 'bold', marginTop: '-1.1rem' }}>
                      {operationStatusMapper(selectedSite.operation_status)} {formatOptSince}
                    </div>
                  </div>
                </div>
              </Col>

              <Col sm={12}>
                <div className={styles.widget} style={{ minHeight: '100px', height: '100%', background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', padding: '1em' }} >
                  <div style={{ fontSize: 22, color: '#6b7280', textAlign: 'right', marginTop: 'auto' }}>State of Charge</div>
                  <div style={{ fontSize: 35, fontWeight: 700, lineHeight: '1em', textAlign: 'right', marginBottom: 'auto' }}>
                    {formatNumber(currentData[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.STATE_OF_CHARGE, 2)])}%
                  </div>
                </div>
              </Col>

              <Col sm={12}>
                <div className={styles.widget} style={{ minHeight: '100px', height: '100%', background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', padding: '1em' }} >
                  <div style={{ fontSize: 22, color: '#6b7280', textAlign: 'right', marginTop: 'auto' }}>State of Health</div>
                  <div style={{ fontSize: 35, fontWeight: 700, lineHeight: '1em', textAlign: 'right', marginBottom: 'auto' }}>
                    {formatNumber(currentData[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.STATE_OF_HEALTH, 2)])}%
                  </div>
                </div>
              </Col>

              <Col sm={12}>
                <div className={styles.widget} style={{ minHeight: '100px', height: '100%', background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', padding: '1em' }} >
                  <div style={{ fontSize: 22, color: '#6b7280', textAlign: 'right', marginTop: 'auto' }}>Power</div>
                  <div style={{ fontSize: 35, fontWeight: 700, lineHeight: '1em', textAlign: 'right', marginBottom: 'auto' }}>
                    {formatNumber(currentData[get_BESS_col_name("ALL", BESS_PARAMS_ENUM.POWER, 2)], 2)} kW
                  </div>
                </div>
              </Col>

              <Col sm={12}>
                <div className={styles.widget} style={{ minHeight: '100px', height: '100%', background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', padding: '1em', maxHeight: '200px' }} >
                  <div style={{ fontSize: 22, color: '#6b7280', textAlign: 'right', marginTop: 'auto' }}>Operation Status</div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: 'auto', maxHeight: '130px', overflowY: 'auto' }}>
                    {currentData._metadata.statusCodeCols.map((col, index) => (
                      <li key={index} style={{ display: 'flex', alignItems: 'center', padding: 0, borderTop: '1px dashed #e6e6e6' }} >
                        <Badge count={currentData[get_BESS_col_name(index + 1, BESS_PARAMS_ENUM.STATUS_CODE)]} showZero />
                        <div style={{ color: '#6b7280', fontSize: 14, marginLeft: 12, textAlign: 'left' }}>
                          <span style={{ fontWeight: 'bold' }}>Cabinet {index + 1}</span> {currentData[get_BESS_col_name(index + 1, BESS_PARAMS_ENUM.STATUS_MSG)]}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            </Row> */}
          </>
        )}
        <Row style={{ paddingTop: '1em' }}>
          <Col md={6} sm={12} xs={12} style={{ margin: 'auto', float: 'none'}}>
            <div>Select Date Range</div>
            <DatePicker.RangePicker
              className={styles.datePicker}
              value={dateRange}
              onChange={onDateRangeChange}
              style={{ width: '100%' }}
              placeholder={['Start Date', 'End Date']}
              format="YYYY-MM-DD"
              disabledDate={disabledDate}
            />
          </Col>
        </Row>

        {/* Show consolidated data */}
        <h3 style={{ paddingTop: '1em' }}>Overall Analytics</h3>
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1em', marginTop: '1em' }}>
          {/* P10 graph monitoring */}
          {(p10Data && p10Data.length > 0) ? (
            <div style={{ marginTop: '2em' }}>
              <ReactEcharts
                option={getP10GraphOpt({ p10DateData, p10SoC, p10Power, p10Volt, p10Current, p10GridPower, p10Temp })}
                style={{ height: '400px', width: 'calc(100% - 4em)', margin: '0 auto' }}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2em' }}>
              <Spin size="large" tip="Loading graph data..." className={styles.spin} />
            </div>
          )}
        </div>
        
        {/* Show cabinet data */}
        <h3 style={{ paddingTop: '1em' }}>Cabinet{selectedSite?.BESS_nb_cabinets >= 2 ? 's' : ''} Analytics</h3>
        <div style={{ marginTop: '1em' }}>
          {currentData ? <>
            <Container fluid>
              <Row gutters={16}>
                {Object.entries(cabinetP10).map(([cabinetNo, cabinetData]) => (
                  <Col key={cabinetNo} md={3} sm={6} xs={12}>
                    <Card title={`Cabinet / Rack ${cabinetNo}`} onClick={() => setSelectedCabinet(cabinetNo)} hoverable style={{ marginBottom: '1em', borderRadius: '1em', border: `5px solid ${currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.GY_STATUS)]?.status_color || '#ffffff'}` }}>
                      <p>State of Charge: {formatNumber(currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.STATE_OF_CHARGE)], 2)}%</p>
                      {!hideSoh ? <p>State of Health: {formatNumber(currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.STATE_OF_HEALTH)], 2)}%</p> : null}
                      <p>Power: {formatNumber(currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.POWER)], 2)} kW</p>
                      <p>Voltage: {formatNumber(currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.VOLTAGE)], 2)} V</p>
                      <p>Current: {formatNumber(currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.CURRENT)], 2)} A</p>
                      <p>Temperature: {formatNumber(currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.TEMPERATURE)], 2)} °C</p>
                      <p>Status: <b>{currentData[get_BESS_col_name(cabinetNo, BESS_PARAMS_ENUM.GY_STATUS)]?.status_name}</b></p>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Container>
          </> : (
            <div style={{ textAlign: 'center', padding: '2em' }}>
              <Spin size="large" tip="Loading graph data..." className={styles.spin} />
            </div>
          )}
        </div>

        {/* Show power of each cabinet in 1 graph */}
        <h3 style={{ paddingTop: '1em' }}>Cabinets Power Analytics</h3>
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1em', marginTop: '1em' }}>
          {cabinetP10 ? (
            <>
              <ReactEcharts
                option={getCabinetsPowerGraphOpt({ p10DateData, cabinetP10 })}
                style={{ height: '400px', width: '100%' }}
              />
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2em' }}>
              <Spin size="large" tip="Loading graph data..." className={styles.spin} />
            </div>
          )}
        </div>

        {/* Cabinet modal graph */}
        <Modal
          title={`Cabinet ${selectedCabinet} Analytics`}
          open={!!selectedCabinetP10Data}
          onCancel={() => setSelectedCabinet(null)}
          footer={null}
          width="90vw"
        >
          {!!selectedCabinet ? (
            <ReactEcharts
              option={getP10GraphOpt({ p10DateData: cabinetP10[selectedCabinet]['dateData'], p10SoC: cabinetP10[selectedCabinet]['soc'], p10Power: cabinetP10[selectedCabinet]['power'], p10Volt: cabinetP10[selectedCabinet]['voltage'], p10Current: cabinetP10[selectedCabinet]['current'], p10GridPower: [], p10Temp: cabinetP10[selectedCabinet]['temp'] })}
              style={{ height: '400px', width: '100%' }}
            />
          ) : null}
        </Modal>

        {/* Daily graph */}
        {!!dailyData ? (
          <>
            <h3 style={{ paddingTop: '2em' }}>Daily Energy Analytics</h3>
            <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1em', marginTop: '1em' }}>
              <ReactEcharts
                option={getBarGraphOpt({ userRoles, dateData: dailyData['dateData'], charge_energy: dailyData['BESS_charge_energy'], discharge_energy: dailyData['BESS_discharge_energy'], charge_energy_on: dailyData['BESS_charge_energy_on'], charge_energy_off: dailyData['BESS_charge_energy_off'], charge_energy_std: dailyData['BESS_charge_energy_std'], discharge_energy_on: dailyData['BESS_discharge_energy_on'], discharge_energy_off: dailyData['BESS_discharge_energy_off'], discharge_energy_std: dailyData['BESS_discharge_energy_std'] })}
                style={{ height: '400px', width: '100%' }}
              />
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <Spin size="large" tip="Loading daily graph data..." className={styles.spin} />
          </div>
        )}

        {/* Monthly data */}
        <Row style={{ paddingTop: '1em' }}>
          <Col md={6} sm={12} xs={12} style={{ margin: 'auto', float: 'none'}}>
            <div>Select Month Range</div>
            <DatePicker.RangePicker
              picker="month"
              className={styles.datePicker}
              value={monthRange}
              onChange={onMonthRangeChange}
              style={{ width: '100%' }}
              placeholder={['Start Date', 'End Date']}
              format="YYYY-MM-DD"
              disabledDate={disabledDate}
            />
          </Col>
        </Row>
        <h3 style={{ paddingTop: '2em' }}>Monthly Energy Analytics</h3>
        {!!monthlyData ? (
          <>
            <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1em', marginTop: '1em' }}>
              <ReactEcharts
                option={getBarGraphOpt({ userRoles, dateData: monthlyData['dateData'], charge_energy: monthlyData['BESS_charge_energy'], discharge_energy: monthlyData['BESS_discharge_energy'], charge_energy_on: monthlyData['BESS_charge_energy_on'], charge_energy_off: monthlyData['BESS_charge_energy_off'], charge_energy_std: monthlyData['BESS_charge_energy_std'], discharge_energy_on: monthlyData['BESS_discharge_energy_on'], discharge_energy_off: monthlyData['BESS_discharge_energy_off'], discharge_energy_std: monthlyData['BESS_discharge_energy_std'], charge_start_index: monthlyData['BESS_charge_start_index'], charge_end_index: monthlyData['BESS_charge_end_index'], discharge_start_index: monthlyData['BESS_discharge_start_index'], discharge_end_index: monthlyData['BESS_discharge_end_index'] })}
                style={{ height: '400px', width: '100%' }}
              />
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <Spin size="large" tip="Loading monthly graph data..." className={styles.spin} />
          </div>
        )}

        <div style={{ width: '100%', height: '1em' }}></div>

      </Container>
    </>
  );
}

export default BESSDashboard;